import { WorkspaceStorageHeader } from "./WorkspaceStorageHeader";
import { useEffect, useMemo, useState } from "react";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Check,
  Filter,
  Trash2,
} from "lucide-react";
import type { AgentWorkspaceVolume } from "@lumen/shared";
import { api } from "../../api";
import { t } from "../../i18n";
import { formatBytes } from "../../format-bytes";

export function WorkspaceVolumesPage({ canManage }: { canManage: boolean }) {
  const [volumes, setVolumes] = useState<AgentWorkspaceVolume[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [cleaning, setCleaning] = useState(false);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [hideNotCreated, setHideNotCreated] = useState(true);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [sort, setSort] = useState<{
    key: "volume" | "usedBy" | "runtime" | "size" | "lastUsed" | "status";
    direction: "asc" | "desc";
  }>({ key: "volume", direction: "asc" });

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      setVolumes(await api<AgentWorkspaceVolume[]>("/agent-workspace-volumes"));
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : "Could not load workspace volumes",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const stats = useMemo(
    () => ({
      existing: volumes.filter((volume) => volume.exists).length,
      attached: volumes.filter((volume) => volume.attached).length,
      reclaimable: volumes.filter((volume) => volume.reclaimable).length,
    }),
    [volumes],
  );

  const visibleVolumes = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    const rows = volumes.filter((volume) => {
      if (hideNotCreated && !volume.exists) return false;
      if (!normalizedQuery) return true;
      return [
        volume.name,
        volume.repositoryName,
        volume.workspaceName,
        volume.userName,
        volume.conversationId,
        volume.backend,
        volume.driver,
        volume.kind,
        volume.exists ? "created" : "not created",
        volume.attached ? "attached" : "reclaimable",
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(normalizedQuery));
    });
    const textValue = (volume: AgentWorkspaceVolume) => {
      switch (sort.key) {
        case "usedBy":
          return volume.workspaceName ?? volume.repositoryName ?? "";
        case "runtime":
          return volume.backend + " " + (volume.driver ?? "");
        case "status":
          return volume.exists
            ? volume.attached
              ? "attached"
              : "reclaimable"
            : "not created";
        default:
          return volume.name;
      }
    };
    const value = (volume: AgentWorkspaceVolume) => {
      if (sort.key === "size") return volume.sizeBytes ?? -1;
      if (sort.key === "lastUsed")
        return volume.lastUsedAt ? Date.parse(volume.lastUsedAt) : -1;
      return textValue(volume).toLowerCase();
    };
    return [...rows].sort((left, right) => {
      const leftValue = value(left);
      const rightValue = value(right);
      const comparison =
        typeof leftValue === "number" && typeof rightValue === "number"
          ? leftValue - rightValue
          : String(leftValue).localeCompare(String(rightValue));
      return comparison * (sort.direction === "asc" ? 1 : -1);
    });
  }, [hideNotCreated, query, sort, volumes]);

  const selectableVolumes = visibleVolumes.filter(
    (volume) => canManage && volume.reclaimable,
  );
  const selectedCount = volumes.filter(
    (volume) => selected.has(volume.id) && volume.reclaimable,
  ).length;
  const allVisibleSelected =
    selectableVolumes.length > 0 &&
    selectableVolumes.every((volume) => selected.has(volume.id));

  const toggleSort = (
    key: "volume" | "usedBy" | "runtime" | "size" | "lastUsed" | "status",
  ) => {
    setSort((current) => ({
      key,
      direction:
        current.key === key && current.direction === "asc" ? "desc" : "asc",
    }));
  };

  const toggleSelected = (id: string) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAllVisible = () => {
    setSelected((current) => {
      const next = new Set(current);
      if (allVisibleSelected)
        selectableVolumes.forEach((volume) => next.delete(volume.id));
      else selectableVolumes.forEach((volume) => next.add(volume.id));
      return next;
    });
  };

  const remove = async (volume: AgentWorkspaceVolume, confirm = true) => {
    if (!volume.reclaimable || !canManage) return;
    if (
      confirm &&
      !window.confirm(
        `Remove the stored ${volume.kind} volume “${volume.name}”? This permanently deletes its files.`,
      )
    )
      return;
    setBusy(volume.id);
    setError("");
    try {
      await api(
        `/agent-workspace-volumes/${volume.backend}/${encodeURIComponent(volume.name)}`,
        { method: "DELETE" },
      );
      await load();
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "Could not remove volume",
      );
    } finally {
      setBusy("");
    }
  };

  const removeSelected = async () => {
    const targets = volumes.filter(
      (volume) => selected.has(volume.id) && volume.reclaimable,
    );
    if (!targets.length || !canManage) return;
    if (
      !window.confirm(
        `Remove ${targets.length} selected volume(s)? This permanently deletes their files.`,
      )
    )
      return;
    setBusy("bulk");
    setError("");
    try {
      const results = await Promise.allSettled(
        targets.map((volume) =>
          api(
            `/agent-workspace-volumes/${volume.backend}/${encodeURIComponent(volume.name)}`,
            { method: "DELETE" },
          ),
        ),
      );
      const failed = results.filter((result) => result.status === "rejected");
      if (failed.length)
        setError(`${failed.length} volume(s) could not be removed.`);
      setSelected(new Set());
      await load();
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "Could not remove volumes",
      );
    } finally {
      setBusy("");
    }
  };

  const cleanExpired = async () => {
    if (!canManage) return;
    if (
      !window.confirm(
        "Remove all currently unattached workspace volumes? This permanently deletes their files.",
      )
    )
      return;
    setCleaning(true);
    setError("");
    try {
      const result = await api<{ removed: number; skipped: number }>(
        "/agent-workspace-volumes/gc",
        { method: "POST", body: "{}" },
      );
      await load();
      if (result.removed || result.skipped)
        setError(
          `${result.removed} volume(s) removed; ${result.skipped} skipped.`,
        );
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : "Could not clean up expired volumes",
      );
    } finally {
      setCleaning(false);
    }
  };

  return (
    <section className="min-w-0 flex-1 overflow-y-auto p-5 md:p-8">
      <div className="mx-auto max-w-6xl">
        <WorkspaceStorageHeader
          title={t("Workspace volumes")}
          description={t(
            "Inspect the persistent storage used by repository checkouts and agent workspace runtimes.",
          )}
          actions={
            canManage && (
              <button
                className="button"
                disabled={cleaning}
                onClick={() => void cleanExpired()}
              >
                <Trash2 size={15} />
                {t("Clean up expired")}
              </button>
            )
          }
          loading={loading}
          onRefresh={load}
          error={error}
        />

        <div className="panel mb-5 flex flex-wrap items-center gap-3 p-3">
          <div className="relative min-w-[240px] flex-1">
            <Filter
              size={15}
              className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-stone-400"
            />
            <input
              className="w-full rounded-lg border border-stone-200 bg-white py-2 pr-3 pl-9 text-sm transition outline-none focus:border-lumen-500 dark:border-white/10 dark:bg-white/5"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t("Search volumes")}
              aria-label={t("Search volumes")}
            />
          </div>
          <button
            type="button"
            className={`button text-xs ${hideNotCreated ? "bg-stone-100 dark:bg-white/10" : ""}`}
            onClick={() => setHideNotCreated((value) => !value)}
            aria-pressed={hideNotCreated}
          >
            <Filter size={14} />
            {hideNotCreated
              ? t("Showing created only")
              : t("Including not created")}
          </button>
          {canManage && selectedCount > 0 && (
            <button
              type="button"
              className="button bg-red-600 text-white hover:bg-red-700"
              disabled={busy === "bulk"}
              onClick={() => void removeSelected()}
            >
              <Trash2 size={14} />
              {t("Delete selected")} ({selectedCount})
            </button>
          )}
        </div>

        <div className="mb-5 grid gap-3 sm:grid-cols-3">
          {[
            [t("Known volumes"), stats.existing],
            [t("Attached"), stats.attached],
            [t("Ready to reclaim"), stats.reclaimable],
          ].map(([label, value]) => (
            <div key={label} className="panel p-4">
              <p className="text-xs font-semibold tracking-wide text-stone-400 uppercase">
                {label}
              </p>
              <p className="mt-1 text-2xl font-bold">{value}</p>
            </div>
          ))}
        </div>

        <div className="panel overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-left text-sm">
              <thead className="border-b border-stone-200 bg-stone-50 text-xs tracking-wide text-stone-400 uppercase dark:border-white/10 dark:bg-white/3">
                <tr>
                  {canManage && (
                    <th className="w-12 px-5 py-3">
                      <button
                        type="button"
                        className={`grid size-5 place-items-center rounded-md border transition ${allVisibleSelected ? "border-lumen-600 bg-lumen-600 text-white" : "border-stone-300 bg-white dark:border-white/20 dark:bg-white/5"}`}
                        onClick={toggleAllVisible}
                        aria-label={t("Select all visible reclaimable volumes")}
                        aria-pressed={allVisibleSelected}
                      >
                        {allVisibleSelected && (
                          <Check size={13} strokeWidth={3} />
                        )}
                      </button>
                    </th>
                  )}
                  {[
                    ["volume", t("Volume")],
                    ["usedBy", t("Used by")],
                    ["runtime", t("Runtime")],
                    ["size", t("Size")],
                    ["lastUsed", t("Last used")],
                    ["status", t("Status")],
                  ].map(([key, label]) => (
                    <th key={key} className="px-5 py-3">
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 transition hover:text-stone-700 dark:hover:text-stone-200"
                        onClick={() => toggleSort(key as typeof sort.key)}
                      >
                        {label}
                        {sort.key === key ? (
                          sort.direction === "asc" ? (
                            <ArrowUp size={13} />
                          ) : (
                            <ArrowDown size={13} />
                          )
                        ) : (
                          <ArrowUpDown size={13} className="opacity-40" />
                        )}
                      </button>
                    </th>
                  ))}
                  <th className="px-5 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100 dark:divide-white/5">
                {visibleVolumes.map((volume) => (
                  <tr
                    key={volume.id}
                    className={
                      selected.has(volume.id)
                        ? "bg-lumen-50/50 dark:bg-lumen-950/10"
                        : ""
                    }
                  >
                    {canManage && (
                      <td className="px-5 py-4">
                        {volume.reclaimable && (
                          <button
                            type="button"
                            className={
                              selected.has(volume.id)
                                ? "grid size-5 place-items-center rounded-md border border-lumen-600 bg-lumen-600 text-white"
                                : "grid size-5 place-items-center rounded-md border border-stone-300 bg-white hover:border-lumen-400 dark:border-white/20 dark:bg-white/5"
                            }
                            onClick={() => toggleSelected(volume.id)}
                            aria-label={
                              (selected.has(volume.id)
                                ? "Deselect "
                                : "Select ") + volume.name
                            }
                            aria-pressed={selected.has(volume.id)}
                          >
                            {selected.has(volume.id) && (
                              <Check size={13} strokeWidth={3} />
                            )}
                          </button>
                        )}
                      </td>
                    )}
                    <td className="px-5 py-4">
                      <div className="font-semibold">
                        {volume.repositoryName ??
                          volume.workspaceName ??
                          "Unassigned"}
                      </div>
                      <div className="mt-1 font-mono text-[11px] text-stone-400">
                        {volume.name}
                      </div>
                      <div className="mt-1 text-xs text-stone-400">
                        {volume.kind === "repository"
                          ? t("Repository")
                          : t("Workspace runtime")}
                      </div>
                    </td>
                    <td className="px-5 py-4 text-stone-600 dark:text-stone-300">
                      <div>
                        {volume.workspaceName
                          ? `${volume.workspaceName}${volume.userName ? ` · ${volume.userName}` : ""}`
                          : (volume.repositoryName ?? "—")}
                      </div>
                      {volume.conversationId && (
                        <div className="mt-1 font-mono text-[11px] text-stone-400">
                          {t("Conversation")}: {volume.conversationId}
                        </div>
                      )}
                      {volume.workspaceName && volume.repositoryName && (
                        <div className="mt-1 text-xs text-stone-400">
                          {t("Repository")}: {volume.repositoryName}
                        </div>
                      )}
                    </td>
                    <td className="px-5 py-4 text-stone-600 capitalize dark:text-stone-300">
                      {volume.backend}
                      {volume.driver ? ` · ${volume.driver}` : ""}
                    </td>
                    <td className="px-5 py-4 text-stone-600 dark:text-stone-300">
                      {formatBytes(volume.sizeBytes)}
                    </td>
                    <td className="px-5 py-4 text-stone-600 dark:text-stone-300">
                      {volume.lastUsedAt
                        ? new Date(volume.lastUsedAt).toLocaleString()
                        : "—"}
                    </td>
                    <td className="px-5 py-4">
                      <span
                        className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                          !volume.exists
                            ? "bg-stone-100 text-stone-500 dark:bg-white/10 dark:text-stone-400"
                            : volume.attached
                              ? "bg-lumen-100 text-lumen-700 dark:bg-lumen-900/40 dark:text-lumen-300"
                              : "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300"
                        }`}
                      >
                        {!volume.exists
                          ? t("Not created")
                          : volume.attached
                            ? t("Attached")
                            : t("Reclaimable")}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-right">
                      {canManage && volume.reclaimable && (
                        <button
                          className="button p-2 text-stone-400 hover:text-red-600"
                          disabled={busy === volume.id}
                          onClick={() => void remove(volume)}
                          title={t("Remove volume")}
                        >
                          <Trash2 size={15} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {!loading && visibleVolumes.length === 0 && (
                  <tr>
                    <td
                      colSpan={canManage ? 8 : 7}
                      className="px-5 py-16 text-center text-sm text-stone-400"
                    >
                      {t("No workspace volumes are known yet.")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        <p className="mt-3 text-xs leading-5 text-stone-400">
          {t(
            "Attached volumes are protected. Removing a reclaimable volume permanently deletes its stored checkout or workspace files.",
          )}
        </p>
      </div>
    </section>
  );
}
